import api from '../config/api';

/**
 * Service para manejar las consideraciones de conceptos de planilla
 * Optimización: Enviar conceptos solo la primera vez, luego usar datos guardados
 */
class ConsideracionesConceptosService {
  
  /**
   * Verificar si ya existen conceptos guardados para una planilla
   * @param {number} planillaId - ID de la planilla
   * @returns {Promise<Object>} - { tiene_conceptos, planilla_id, ultima_actualizacion, conteo_conceptos }
   */
  async verificarConceptosExistentes(planillaId) {
    try {
      console.log(`🔍 Verificando conceptos existentes para planilla ${planillaId}...`);
      
      const response = await api.get(`/planillas/${planillaId}/verificar-conceptos`);
      
      console.log('✅ Verificación de conceptos completada:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Error verificando conceptos existentes:', error);
      throw error;
    }
  }

  /**
   * Enviar conceptos de empleados al backend para guardar
   * @param {number} planillaId - ID de la planilla
   * @param {Array} empleados - Array de empleados con sus conceptos
   * @returns {Promise<Object>} - Resultado del guardado
   */
  async guardarConceptosPlanilla(planillaId, empleados) {
    try {
      console.log(`💾 Enviando conceptos de ${empleados.length} empleados para planilla ${planillaId}...`);
      
      // Transformar empleados al formato esperado por el backend
      const empleadosParaEnviar = empleados.map(empleado => {
        console.log(`🔄 Procesando empleado ${empleado.nombre_completo || empleado.nombre}...`);
        
        // Obtener todos los conceptos del empleado (ingresos, descuentos, aportes)
        const todosLosConceptos = [];
        
        // Procesar conceptos (pueden venir en diferentes formatos)
        const conceptos = empleado.conceptos || [];
        
        if (Array.isArray(conceptos)) {
          // Si conceptos es un array directo
          conceptos.forEach(concepto => {
            if (concepto.plame_configuracion_id && concepto.tipo_concepto) {
              todosLosConceptos.push({
                plame_configuracion_id: concepto.plame_configuracion_id,
                tipo_concepto: concepto.tipo_concepto,
                monto: parseFloat(concepto.monto || 0)
              });
            }
          });
        } else {
          // Si conceptos está dividido en ingresos, descuentos, aportes
          ['ingresos', 'descuentos', 'aportes'].forEach(tipo => {
            const conceptosTipo = conceptos[tipo] || [];
            conceptosTipo.forEach(concepto => {
              if (concepto.plame_configuracion_id) {
                todosLosConceptos.push({
                  plame_configuracion_id: concepto.plame_configuracion_id,
                  tipo_concepto: tipo === 'ingresos' ? 'ingreso' : tipo === 'descuentos' ? 'descuento' : 'aporte',
                  monto: parseFloat(concepto.monto || 0)
                });
              }
            });
          });
        }
        
        console.log(`📋 Empleado ${empleado.nombre_completo || empleado.nombre}: ${todosLosConceptos.length} conceptos procesados`);
        
        return {
          id: empleado.id,
          conceptos: todosLosConceptos
        };
      });

      // Filtrar empleados que tienen conceptos
      const empleadosConConceptos = empleadosParaEnviar.filter(emp => emp.conceptos.length > 0);
      
      console.log(`📊 Resumen para envío:`, {
        empleados_totales: empleados.length,
        empleados_con_conceptos: empleadosConConceptos.length,
        total_conceptos: empleadosConConceptos.reduce((sum, emp) => sum + emp.conceptos.length, 0)
      });

      if (empleadosConConceptos.length === 0) {
        throw new Error('No hay empleados con conceptos válidos para enviar');
      }

      const response = await api.post(`/planillas/${planillaId}/guardar-conceptos`, {
        empleados: empleadosConConceptos
      });
      
      console.log('✅ Conceptos guardados exitosamente:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Error guardando conceptos:', error);
      throw error;
    }
  }

  /**
   * Obtener datos de conceptos listos para cálculo desde el backend
   * @param {number} planillaId - ID de la planilla
   * @returns {Promise<Object>} - Datos procesados para cálculo
   */
  async obtenerDatosParaCalculo(planillaId) {
    try {
      console.log(`📥 Obteniendo datos guardados para cálculo de planilla ${planillaId}...`);
      
      const response = await api.get(`/planillas/${planillaId}/datos-calculo`);
      
      console.log('✅ Datos para cálculo obtenidos:', {
        empleados: response.data.data.empleados.length,
        estadisticas: response.data.data.estadisticas
      });
      
      return response.data;
      
    } catch (error) {
      console.error('❌ Error obteniendo datos para cálculo:', error);
      throw error;
    }
  }

  /**
   * Eliminar todos los conceptos guardados de una planilla
   * @param {number} planillaId - ID de la planilla
   * @returns {Promise<Object>} - Resultado de la eliminación
   */
  async eliminarConceptosPlanilla(planillaId) {
    try {
      console.log(`🗑️ Eliminando conceptos guardados de planilla ${planillaId}...`);
      
      const response = await api.delete(`/planillas/${planillaId}/conceptos`);
      
      console.log('✅ Conceptos eliminados exitosamente:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Error eliminando conceptos:', error);
      throw error;
    }
  }

  /**
   * Obtener conceptos de un empleado específico desde la tabla guardada
   * @param {number} planillaId - ID de la planilla
   * @param {number} empleadoId - ID del empleado
   * @returns {Promise<Object>} - Conceptos del empleado
   */
  async obtenerConceptosEmpleado(planillaId, empleadoId) {
    try {
      console.log(`📋 Obteniendo conceptos de empleado ${empleadoId} en planilla ${planillaId}...`);
      
      const response = await api.get(`/planillas/${planillaId}/empleados/${empleadoId}/conceptos`);
      
      console.log('✅ Conceptos de empleado obtenidos:', {
        empleado_id: empleadoId,
        total_conceptos: response.data.data.estadisticas?.total_conceptos || 0,
        ingresos: response.data.data.conceptos.ingresos.length,
        descuentos: response.data.data.conceptos.descuentos.length,
        aportes: response.data.data.conceptos.aportes.length
      });
      
      return response.data;
      
    } catch (error) {
      console.error('❌ Error obteniendo conceptos de empleado:', error);
      
      // Si es 404, el empleado no tiene conceptos guardados
      if (error.response?.status === 404) {
        return {
          success: false,
          data: {
            planilla_id: planillaId,
            empleado_id: empleadoId,
            conceptos: {
              ingresos: [],
              descuentos: [],
              aportes: []
            }
          }
        };
      }
      
      throw error;
    }
  }

  /**
   * Flujo completo optimizado: Verificar, guardar si es necesario, y obtener datos
   * @param {number} planillaId - ID de la planilla
   * @param {Array} empleados - Array de empleados (solo necesario si no existen datos)
   * @returns {Promise<Object>} - Datos listos para cálculo
   */
  async flujoOptimizadoConceptos(planillaId, empleados = null) {
    try {
      console.log(`🚀 === INICIANDO FLUJO OPTIMIZADO PARA PLANILLA ${planillaId} ===`);
      
      // PASO 1: Verificar si ya existen conceptos
      const verificacion = await this.verificarConceptosExistentes(planillaId);
      
      if (verificacion.data.tiene_conceptos) {
        // CASO A: Ya existen datos → Usar datos guardados
        console.log('✅ DATOS EXISTENTES ENCONTRADOS - Usando conceptos guardados');
        console.log('📊 Información de datos existentes:', {
          ultima_actualizacion: verificacion.data.ultima_actualizacion,
          conteo_conceptos: verificacion.data.conteo_conceptos
        });
        
        const datosGuardados = await this.obtenerDatosParaCalculo(planillaId);
        return {
          source: 'datos_guardados',
          datos: datosGuardados.data,
          info: verificacion.data
        };
        
      } else {
        // CASO B: No existen datos → Enviar y guardar conceptos
        console.log('❌ NO EXISTEN DATOS GUARDADOS - Enviando conceptos al backend');
        
        if (!empleados || empleados.length === 0) {
          throw new Error('Se requieren datos de empleados para el primer envío');
        }
        
        // Guardar conceptos en el backend
        const resultadoGuardado = await this.guardarConceptosPlanilla(planillaId, empleados);
        
        // Obtener los datos recién guardados
        const datosGuardados = await this.obtenerDatosParaCalculo(planillaId);
        
        return {
          source: 'datos_nuevos',
          datos: datosGuardados.data,
          guardado: resultadoGuardado.data
        };
      }
      
    } catch (error) {
      console.error('❌ Error en flujo optimizado:', error);
      throw error;
    }
  }

  /**
   * Restaurar conceptos originales de un empleado desde su configuración
   * @param {number} empleadoId - ID del empleado
   * @returns {Promise<Object>} - Conceptos originales agrupados por tipo
   */
  async restaurarConceptosOriginalesEmpleado(empleadoId) {
    try {
      console.log(`🔄 Solicitando restauración de conceptos originales para empleado ${empleadoId}...`);
      
      const response = await api.get(`/empleados/${empleadoId}/conceptos-originales`);
      
      console.log('✅ Conceptos originales obtenidos:', {
        empleado_id: empleadoId,
        total_conceptos: response.data.data.estadisticas?.total_conceptos || 0,
        ingresos: response.data.data.conceptos.ingresos.length,
        descuentos: response.data.data.conceptos.descuentos.length,
        aportes: response.data.data.conceptos.aportes.length
      });
      
      return response.data;
      
    } catch (error) {
      console.error('❌ Error restaurando conceptos originales:', error);
      throw error;
    }
  }
}

export default new ConsideracionesConceptosService();

